/* eslint-disable react-hooks/rules-of-hooks */
'use client'

import { Fragment, useMemo } from 'react'

import Flex from '@wiggui/ds/Flex'
import Button from '@wiggui/ds/Button'
import GoogleIcon from '@wiggui/ds/GoogleIcon'
import Input from '@wiggui/ds/Input'
import Modal from '@wiggui/ds/Modal'
import Spinner from '@wiggui/ds/Spinnner'
import Text from '@wiggui/ds/Text'
import Dropdown from '@wiggui/ds/Dropdown'
import { composeClasses, createUrlMedia } from '@wiggot/utils'
import { useReassignAdviserV2 } from '../hooks/useModalReassignAdviserV2'
import Portal from '@wiggui/ds/Portal'
import OptionButton from '@wiggui/ds/OptionButton'
import {
  MdOutlineClose,
  MdOutlineDone,
  MdOutlineElectricBolt
} from 'react-icons/md'
import Avatar from '@wiggui/ds/Avatar'
import { ItemUser } from '../../../../interfaces/src/property-v2.interface'
import Tooltip from '@wiggui/ds/Tooltip'

const BrokerSelected = ({
  brokers
}: {
  brokers: {
    fullName: string
    photo?: string
  }[]
}) => {
  const getBrokerInitials = (name: string): string => {
    if (name) {
      const arrayName = name.split(' ')
      return `${arrayName[0]?.charAt(0) ?? ''} ${arrayName[1]?.charAt(0) ?? ''}`
    }
    return ''
  }

  const getPhotoBroker = (
    fullName: string,
    photo?: string,
    padding?: boolean
  ) => {
    if (photo) {
      return (
        <img
          className={`w-[32px] h-[32px] rounded-full ${
            padding && 'border-white border-[3px]'
          }`}
          alt={photo}
          src={createUrlMedia({ id: photo })}
        />
      )
    }
    return (
      <div
        className={`w-[32px] h-[32px] text-[10px] font-semibold rounded-full uppercase flex items-center justify-center bg-[#D5D8DE] ${
          padding && 'border-white border-[3px]'
        }`}
      >
        {getBrokerInitials(fullName)}
      </div>
    )
  }

  if (brokers.length === 1) {
    return (
      <div className="w-full flex items-center gap-2">
        {getPhotoBroker(brokers[0].fullName, brokers[0].photo)}
        <p>{brokers[0].fullName}</p>
      </div>
    )
  }

  const multiplePhotos = useMemo(() => {
    if (brokers.length > 2) {
      return [...brokers.slice(0, 2), { fullName: `${brokers.length - 2} +` }]
    }
    return brokers
  }, [brokers])

  return (
    <div className="w-full flex items-center gap-2">
      <div className="flex space-x-[-15px]">
        {multiplePhotos.map((item) => {
          return (
            <Fragment key={item.fullName}>
              {getPhotoBroker(item.fullName, item?.photo, true)}
            </Fragment>
          )
        })}
      </div>
      <p>Múltiples asesores</p>
    </div>
  )
}

export const ModalReassignAdviserV2 = () => {
  const {
    adviserSelected,
    filteredOptions,
    handleChangeAdvisor,
    isLoading,
    nameAdviser,
    onClose,
    setNameAdviser,
    handleAssignedAdvisor,
    isLoadingReassignAdviser,
    modalReassignAdviserV2,
    setShow,
    show,
    type,
    mode
  } = useReassignAdviserV2()

  const getTextBroker = useMemo(() => {
    if (adviserSelected) {
      return (
        <BrokerSelected
          brokers={[
            {
              fullName: adviserSelected?.fullName,
              photo: adviserSelected?.photo ?? undefined
            }
          ]}
        />
      )
    }
    if (modalReassignAdviserV2?.propertiesIds?.length > 0) {
      const brokersArr =
        (modalReassignAdviserV2?.brokersData &&
          modalReassignAdviserV2?.brokersData
            .map((item) => ({
              fullName: item?.fullName || '',
              photo: item?.photo
            }))
            .filter((obj) => Boolean(obj.fullName))
            .filter(
              (obj, index, self) =>
                index ===
                self.findIndex(
                  (t) => obj.fullName && t.fullName === obj.fullName
                )
            )) ||
        []
      if (brokersArr?.length > 0) {
        return <BrokerSelected brokers={brokersArr} />
      }
      return <Text className="text-base">Selecciona un asesor</Text>
    }
    return <Text className="text-base">Selecciona un asesor</Text>
  }, [modalReassignAdviserV2, adviserSelected])

  const calculateTitle = useMemo(() => {
    if (type === 'assign') {
      return modalReassignAdviserV2?.propertiesIds?.length > 1
        ? 'Asignar propiedades'
        : 'Asignar propiedad'
    }

    return modalReassignAdviserV2?.propertiesIds?.length > 1
      ? 'Reasignar propiedades'
      : 'Reasignar propiedad'
  }, [modalReassignAdviserV2, type])

  if (!modalReassignAdviserV2?.isOpen) {
    return null
  }

  if (mode === 'in-modal') {
    return (
      <Portal className="backdrop-blur-10 bg-overlay-dark fixed top-0 bottom-0 left-0 right-0 !z-20 flex items-end md:items-center justify-center">
        <div className="flex flex-col items-center justify-start md:justify-center md:max-w-[684px] shadow-y5 !z-[20] w-full bg-white rounded-t-xl md:rounded-b-xl">
          <div className="border-b border-surface-strokes pt-3 pb-1 px-6 flex flex-row w-full items-center justify-between">
            {calculateTitle}
            <OptionButton
              onClick={onClose}
              onlyIcon={
                <MdOutlineClose className="min-w-[24px] min-h-[24px] text-primary" />
              }
            />
          </div>
          <div className="flex flex-col items-center justify-start gap-8 w-full p-6 max-h-[80vh] ui-kit-scrollbar overflow-y-auto h-full">
            <Text className="text-primary text-body-m w-full" align="left">
              Busca un asesor para asignarle{' '}
              {modalReassignAdviserV2?.propertiesIds?.length > 1
                ? 'estas propiedades'
                : 'esta propiedad'}
            </Text>
            <Flex dir="col" items="start" justify="center" gap="gap-4" wFull>
              <Text className="text-body-bold-l text-primary text-left">
                Asesor
              </Text>
              <Dropdown
                closeOnEscape
                closeOnOutsideClick
                isResponsive={false}
                positionY={10}
                isOpen={show}
                setIsOpen={setShow}
                onClose={() => setShow(false)}
                className="w-full max-w-[640px]"
                strategyPosition="fixed"
                defaultPlacement="top"
              >
                <Dropdown.Trigger className="w-full">
                  <button
                    type="button"
                    className="w-full hover:bg-surface-gray-10 active:bg-surface-gray-20 h-[52px] border rounded-lg px-4 flex items-center justify-between"
                  >
                    {getTextBroker}
                    <GoogleIcon
                      name="chevron_right"
                      className={composeClasses(
                        show ? '-rotate-90' : 'rotate-90',
                        'transition-all duration-300 ease-in-out'
                      )}
                    />
                  </button>
                </Dropdown.Trigger>
                <Dropdown.Menu className="max-w-[640px]">
                  <div className="w-full flex flex-col gap-4 px-4 items-center justify-start bg-white rounded-lg max-h-[235px] ui-kit-scrollbar overflow-y-auto">
                    <Input
                      name="name-adviser"
                      value={nameAdviser}
                      onChange={(event) => setNameAdviser(event?.target.value)}
                      className="w-full"
                      classNameContainer="w-full"
                      classNameLabel="text-secondary"
                      labelText="Buscar asesor"
                      leftIcon={
                        <GoogleIcon
                          name="search"
                          className="text-primary text-[24px]"
                        />
                      }
                    />
                    {isLoading && (
                      <Flex
                        items="center"
                        justify="center"
                        wFull
                        className="py-2"
                      >
                        <Spinner color="terciary" />
                      </Flex>
                    )}
                    {!isLoading &&
                      nameAdviser &&
                      nameAdviser?.length > 0 &&
                      filteredOptions?.length === 0 && (
                        <Text
                          align="center"
                          className="text-body-bold-m text-primary"
                        >
                          No se encontraron resultados
                        </Text>
                      )}
                    <Flex dir="col" items="start" wFull justify="center">
                      {filteredOptions &&
                        filteredOptions?.length > 0 &&
                        filteredOptions?.map((item) => {
                          const itemIsNotAdvanced = !item?.hasActiveAdvancedPlan

                          if (itemIsNotAdvanced) {
                            return (
                              <Tooltip
                                key={item?.id + item?.fullName}
                                content="El asesor necesita el plan avanzado para gestionar prospectos"
                                className="w-full"
                                tooltipClassName="max-w-[200px]"
                              >
                                <ButtonAssessor
                                  key={item?.id + item?.fullName}
                                  item={item}
                                  itemIsNotAdvanced={itemIsNotAdvanced}
                                  adviserSelected={adviserSelected}
                                  handleChangeAdvisor={handleChangeAdvisor}
                                  setShow={setShow}
                                  isLoadingReassignAdviser={
                                    isLoadingReassignAdviser
                                  }
                                />
                              </Tooltip>
                            )
                          }

                          return (
                            <ButtonAssessor
                              key={item?.id + item?.fullName}
                              item={item}
                              itemIsNotAdvanced={itemIsNotAdvanced}
                              adviserSelected={adviserSelected}
                              handleChangeAdvisor={handleChangeAdvisor}
                              setShow={setShow}
                              isLoadingReassignAdviser={
                                isLoadingReassignAdviser
                              }
                            />
                          )
                        })}
                    </Flex>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </Flex>
          </div>
          <div className="w-full flex flex-row pb-[9px] pt-[9px] px-6 gap-2 border-t border-t-surface-strokes">
            <Button
              variant="terciary"
              fullWidth
              onClick={
                isLoadingReassignAdviser.isLoading ? () => ({}) : onClose
              }
            >
              Cancelar
            </Button>
            <Button
              variant="secondary"
              onClick={handleAssignedAdvisor}
              fullWidth
              isLoading={isLoadingReassignAdviser.isLoading}
              isDisabled={
                isLoadingReassignAdviser.isLoading || !adviserSelected
              }
            >
              Aceptar
            </Button>
          </div>
        </div>
      </Portal>
    )
  }

  return (
    <Modal
      className="w-full max-w-lg relative"
      enableBgBlur
      buttomSheetClassName="pb-5 relative"
      onClose={isLoading || isLoadingReassignAdviser ? () => ({}) : onClose}
      isOpen={modalReassignAdviserV2?.isOpen}
    >
      <Modal.Header title={calculateTitle} isShowCloseButton isShowDivider />
      <Modal.Body className="p-6 w-full flex gap-6 flex-col items-center justify-center">
        <Text className="text-primary text-body-m w-full" align="left">
          Busca un asesor para asignarle{' '}
          {modalReassignAdviserV2?.propertiesIds?.length > 1
            ? 'estas propiedades'
            : 'esta propiedad'}
        </Text>
        <Flex dir="col" items="start" justify="center" gap="gap-4" wFull>
          <Text className="text-body-bold-l text-primary text-left">
            Asesor
          </Text>
          <Dropdown
            closeOnEscape
            closeOnOutsideClick
            isResponsive={false}
            positionY={10}
            isOpen={show}
            setIsOpen={setShow}
            onClose={() => setShow(false)}
            className="w-full"
            defaultPlacement="top"
          >
            <Dropdown.Trigger className="w-full">
              <button
                type="button"
                className="w-full hover:bg-surface-gray-10 active:bg-surface-gray-20 h-[52px] border rounded-lg px-4 flex items-center justify-between"
              >
                {getTextBroker}
                <GoogleIcon
                  name="chevron_right"
                  className={composeClasses(
                    show ? '-rotate-90' : 'rotate-90',
                    'transition-all duration-300 ease-in-out'
                  )}
                />
              </button>
            </Dropdown.Trigger>
            <Dropdown.Menu>
              <div className="w-full flex flex-col gap-4 px-4 items-center justify-start bg-white rounded-lg max-h-[235px] ui-kit-scrollbar overflow-y-auto">
                <Input
                  name="name-adviser"
                  value={nameAdviser}
                  onChange={(event) => setNameAdviser(event?.target.value)}
                  className="w-full"
                  classNameContainer="w-full"
                  classNameLabel="text-secondary"
                  labelText="Buscar asesor"
                  leftIcon={
                    <GoogleIcon
                      name="search"
                      className="text-primary text-[24px]"
                    />
                  }
                />
                {isLoading && (
                  <Flex items="center" justify="center" wFull className="py-2">
                    <Spinner color="terciary" />
                  </Flex>
                )}
                {!isLoading &&
                  nameAdviser &&
                  nameAdviser?.length > 0 &&
                  filteredOptions?.length === 0 && (
                    <Text
                      align="center"
                      className="text-body-bold-m text-primary"
                    >
                      No se encontraron resultados
                    </Text>
                  )}
                <Flex dir="col" items="start" wFull justify="center">
                  {filteredOptions &&
                    filteredOptions?.length > 0 &&
                    filteredOptions?.map((item) => {
                      const itemIsNotAdvanced = !item?.hasActiveAdvancedPlan

                      if (itemIsNotAdvanced) {
                        return (
                          <Tooltip
                            key={item?.id + item?.fullName}
                            content="El asesor necesita el plan avanzado para gestionar prospectos"
                            className="w-full"
                            tooltipClassName="max-w-[200px]"
                          >
                            <ButtonAssessor
                              key={item?.id + item?.fullName}
                              item={item}
                              itemIsNotAdvanced={itemIsNotAdvanced}
                              adviserSelected={adviserSelected}
                              handleChangeAdvisor={handleChangeAdvisor}
                              setShow={setShow}
                              isLoadingReassignAdviser={
                                isLoadingReassignAdviser
                              }
                            />
                          </Tooltip>
                        )
                      }

                      return (
                        <ButtonAssessor
                          key={item?.id + item?.fullName}
                          item={item}
                          itemIsNotAdvanced={itemIsNotAdvanced}
                          adviserSelected={adviserSelected}
                          handleChangeAdvisor={handleChangeAdvisor}
                          setShow={setShow}
                          isLoadingReassignAdviser={isLoadingReassignAdviser}
                        />
                      )
                    })}
                </Flex>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </Flex>
      </Modal.Body>
      <Modal.Footer
        className="w-full flex flex-row py-[9px] px-6 gap-2"
        isShowDivider
      >
        <Button
          variant="terciary"
          fullWidth
          onClick={isLoadingReassignAdviser.isLoading ? () => ({}) : onClose}
        >
          Cancelar
        </Button>
        <Button
          variant="secondary"
          onClick={handleAssignedAdvisor}
          fullWidth
          isLoading={isLoadingReassignAdviser.isLoading}
          isDisabled={isLoadingReassignAdviser.isLoading || !adviserSelected}
        >
          Aceptar
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export const ButtonAssessor = ({
  item,
  itemIsNotAdvanced,
  adviserSelected,
  handleChangeAdvisor,
  setShow,
  isLoadingReassignAdviser
}: {
  item: ItemUser
  itemIsNotAdvanced: boolean
  adviserSelected: ItemUser | undefined
  handleChangeAdvisor: (item: ItemUser) => void
  setShow: (show: boolean) => void
  isLoadingReassignAdviser: { isLoading: boolean; userId: number | undefined }
}) => {
  const avatarUrl = item?.photo
    ? createUrlMedia({ id: item?.photo })
    : undefined

  return (
    <button
      key={item?.id + item?.fullName}
      type="button"
      onClick={(event) => {
        event.preventDefault()
        event.stopPropagation()
        handleChangeAdvisor(item)
        setShow(false)
      }}
      disabled={itemIsNotAdvanced}
      className={composeClasses(
        'flex justify-between items-center gap-2 w-full text-sm p-2',
        itemIsNotAdvanced
          ? 'bg-button-primary-disabled cursor-not-allowed'
          : 'bg-white active:bg-surface-gray-20 hover:bg-surface-gray-10 hover:font-semibold group'
      )}
    >
      <div className="flex items-center gap-2 overflow-hidden">
        <Avatar
          avatarUrl={avatarUrl}
          fullName={avatarUrl ? undefined : item?.fullName}
          containerClassName="min-w-[32px] max-w-[32px] min-h-[32px] max-h-[32px]"
        />

        <span
          className={composeClasses(
            'text-left group-hover:font-semibold text-body-m overflow-hidden whitespace-nowrap text-ellipsis',
            itemIsNotAdvanced ? 'text-terciary' : 'text-primary',
            adviserSelected?.id === item?.id && 'font-semibold'
          )}
        >
          {item.fullName ?? item.email}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {itemIsNotAdvanced ? (
          <div className="bg-white px-2 rounded-full">
            <span className="text-body-xs font-bold text-primary">
              SIN PLAN ACTIVO
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1 bg-notif-yellow-01 px-2 py-[2.5px] rounded-full">
            <MdOutlineElectricBolt className="text-notif-yellow" size={16} />
            <span className="text-body-xs font-bold text-notif-yellow">
              AVANZADO
            </span>
          </div>
        )}

        {adviserSelected?.id === item?.id && (
          <MdOutlineDone className="text-primary" size={18} />
        )}
      </div>

      {isLoadingReassignAdviser?.isLoading &&
        isLoadingReassignAdviser?.userId === item?.id && (
          <Flex justify="center" items="end">
            <Spinner customBorderColor="border-t-primary" />
          </Flex>
        )}
    </button>
  )
}
